import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
import { InjectedFormProps, reduxForm, SubmissionError } from 'redux-form'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// components
import { ReduxInput } from 'components/FormElements/ReduxForm'
import { Button, ErrorMessage, Link } from 'components/UI'
// constants
import { Forms } from 'constants/forms'
// types
import { TwoFactorErrorCodes } from 'types/auth/helpers'
import { IAuthVerifyTwoFactorResponse } from 'types/graphql'
// graphql
import { AUTH_VERIFY_TWO_FACTOR_MUTATION } from 'graphql/auth/twoFactor/mutations'
// redux
import { setCurrentUser } from 'store/reducers/currentUser'
import { clearTwoFactorToken } from 'store/reducers/twoFactorAuth'
// views
import { AuthForm, AuthHeader, ButtonWrapper } from 'themes/views/auth'
import { HelpText } from './views'
// utils
import { requiredEmpty } from 'utils/validators'
import { parseGraphQLErrorMessages, parseGraphQLErrorCodes } from 'utils/errors/parsers'
import { clearRedirectUrl, getRedirectUrl } from 'utils/cookies'
import { setAccessToken } from 'lib/auth'

type FormData = {
  code: string
}

const TwoFactorVerify: React.FC<InjectedFormProps<FormData>> = ({ valid, error, handleSubmit }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { client } = useApolloClient()

  const [verifyMutation] = useMutation<IAuthVerifyTwoFactorResponse>(AUTH_VERIFY_TWO_FACTOR_MUTATION)

  const { twoFactorToken, challengeExpiry } = useSelector((state: any) => state.twoFactorAuth)

  React.useEffect(() => {
    // Redirect to login if no token or expired
    if (!twoFactorToken || (challengeExpiry && Date.now() > challengeExpiry)) {
      dispatch(clearTwoFactorToken())
      router.push('/login')
    }
  }, [twoFactorToken, challengeExpiry, dispatch, router])

  const handleVerify = async (formData: FormData) => {
    try {
      const {
        data: {
          authVerifyTwoFactor: { data }
        }
      } = await verifyMutation({
        variables: {
          twoFactorToken,
          code: formData.code.trim()
        }
      })

      // Clear 2FA token from memory
      dispatch(clearTwoFactorToken())

      // Set user and tokens
      dispatch(setCurrentUser(data.user))
      setAccessToken(data.accessToken)

      // Clear Apollo cache and get fresh data
      await client.clearStore()

      // Redirect to home or saved URL
      const redirectUrl = getRedirectUrl()

      if (redirectUrl) {
        clearRedirectUrl()
        await router.push(redirectUrl)
      } else {
        await router.push('/home')
      }
    } catch (errors) {
      const errorCodes = parseGraphQLErrorCodes(errors)

      // Handle token expiry
      if (errorCodes.includes(TwoFactorErrorCodes.TWO_FACTOR_CHALLENGE_EXPIRED)) {
        dispatch(clearTwoFactorToken())
        await router.push('/login')
        return
      }

      throw new SubmissionError({
        _error: parseGraphQLErrorMessages(errors)
      })
    }
  }

  const onBackCLick = () => dispatch(clearTwoFactorToken())

  return (
    <>
      <AuthHeader>TWO-FACTOR AUTHENTICATION</AuthHeader>
      <AuthForm onSubmit={handleSubmit(handleVerify)}>
        <HelpText>Enter the 6-digit code from your authenticator app, or use one of your backup codes.</HelpText>

        <ReduxInput
          type='text'
          name='code'
          label='Verification Code'
          validate={[requiredEmpty]}
          autoComplete='off'
          autoFocus
        />
        <Link href='/login' onClick={onBackCLick}>
          Return to login
        </Link>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonWrapper>
          <Button variant='contained' fullWidth type='submit' disabled={!valid}>
            VERIFY
          </Button>
        </ButtonWrapper>
      </AuthForm>
    </>
  )
}

export default reduxForm<FormData>({ form: Forms.TWO_FACTOR_VERIFY_FORM })(TwoFactorVerify)
